import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { useState } from "react";
import { useCabinetId, useCabinetMemberMe, CABINET_MEMBER_ME_KEY } from '@/hooks/useCabinetMemberMe';
import { apiClient } from "@/lib/apiClient";
import { ErrorService } from "@/lib/errors/ErrorService";
import type { CheckoutSessionResponse, PortalSessionResponse } from "@hector/shared-types";

export interface CabinetSubscription {
  id: string;
  cabinet_id: string;
  status: string;
  price_id: string;
  quantity: number;
  current_period_end: string;
  cancel_at_period_end: boolean;
  trial_end: string | null;
  has_completed_onboarding: boolean;
  successful_checkout_firsttime: boolean;
  primary_billing_admin_id: string;
  backup_billing_admin_id: string | null;
  created_at: string;
  updated_at: string;
  license_end_date?: string | null;
  cancellation_reason?: string | null;
}

export interface LicenseUsage {
  max_licenses: number;
  used_licenses: number;
  available_licenses: number;
  licenses_ending_soon?: number;
  next_reduction_date?: string;
}

/**
 * Cabinet subscription hook — reads through the backend API, mutates through
 * `/api/stripe/*` (P1b/P4 rewrite, 2026-08-12).
 *
 * Ownership contract: `cabinet_subscriptions` is a **Stripe webhook-fed
 * cache**. The frontend never writes subscription state — the Supabase Edge
 * Functions (create-cabinet-customer / create-cabinet-checkout-session /
 * create-cabinet-portal-session) and the direct `.from('cabinet_subscriptions')`
 * writes this hook used to carry are gone. Checkout/Portal now return a hosted
 * `url` from the backend (price ids live server-side in env) and the browser
 * simply navigates to it.
 */
export const useCabinetSubscription = (cabinetId?: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  // Simplified condition check - only disable during actual profile completion
  const isOnProfileCompletionPage = window.location.pathname === '/complete-profile';
  const shouldDisableQueries = !cabinetId || isOnProfileCompletionPage;

  // Fetch cabinet subscriptions (multiple subscriptions per cabinet)
  const fetchCabinetSubscriptions = async (): Promise<CabinetSubscription[]> => {
    if (!user || !cabinetId) return [];

    try {
      const subscriptions = await apiClient.get<CabinetSubscription[]>(`/cabinets/${cabinetId}/subscriptions`);

      // Filter out expired trial subscriptions
      const validSubscriptions = (subscriptions || []).filter((sub: CabinetSubscription) => {
        if (sub.status === 'trialing' && sub.trial_end && new Date(sub.trial_end).getTime() < new Date().getTime()) {
          return false;
        }
        return true;
      });

      return validSubscriptions;
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'fetch_cabinet_subscriptions' }, silent: true });
      return [];
    }
  };

  // Fetch license usage
  const fetchLicenseUsage = async (): Promise<LicenseUsage | null> => {
    if (!user || !cabinetId) return null;

    try {
      const result = await apiClient.post<unknown>('/rpc/get_license_usage', { p_cabinet_id: cabinetId });
      const licenseData = Array.isArray(result) ? result[0] : result;
      return (licenseData as LicenseUsage) || { max_licenses: 0, used_licenses: 0, available_licenses: 0 };
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'fetch_license_usage' }, silent: true });
      return null;
    }
  };

  const subscriptionsQuery = useQuery({
    queryKey: ["cabinet-subscriptions", cabinetId],
    queryFn: fetchCabinetSubscriptions,
    enabled: !!user && !!cabinetId && !shouldDisableQueries,
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent unnecessary refetches
    gcTime: 10 * 60 * 1000, // 10 minutes in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on every mount if data is fresh
    retry: (failureCount, _error) => {
      // Retry up to 3 times for new cabinets (subscription might not be created yet)
      if (failureCount < 3) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff, max 30s
  });

  const licenseUsageQuery = useQuery({
    queryKey: ["license-usage", cabinetId],
    queryFn: fetchLicenseUsage,
    enabled: !!user && !!cabinetId && !shouldDisableQueries,
    staleTime: 2 * 60 * 1000, // 2 minutes - license usage can change more frequently
    gcTime: 5 * 60 * 1000, // 5 minutes in cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, _error) => {
      // Retry up to 3 times for new cabinets
      if (failureCount < 3) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Cabinet member data — read from centralized cache
  const { data: memberMe } = useCabinetMemberMe();

  // Computed values
  const subscriptions = subscriptionsQuery.data || [];
  const subscription = subscriptions[0] || null; // Primary subscription for backward compatibility
  const licenseUsage = licenseUsageQuery.data;
  const isAdmin = memberMe?.cabinet_role === 'admin';
  const wasInvited = memberMe?.was_invited ?? false;

  // Get has_completed_onboarding from subscription data (cabinet-level)
  const hasCompletedOnboarding = subscription?.has_completed_onboarding === true;

  // Aggregate license count from all subscriptions (exclude canceled)
  const totalLicenses = subscriptions.filter(sub => sub.status !== 'canceled').reduce((sum, sub) => sum + sub.quantity, 0);

  // Create billing breakdown for display - separate active and ending subscriptions
  const billingBreakdown = (() => {
    if (!subscriptions.length) return [];

    // Separate active (continuing) and ending subscriptions (exclude canceled)
    const activeSubscriptions = subscriptions.filter(sub => !sub.cancel_at_period_end && sub.status !== 'canceled');
    const endingSubscriptions = subscriptions.filter(sub => sub.cancel_at_period_end && sub.status !== 'canceled');

    // Group active subscriptions by exact billing date
    const groupedActiveByDate = activeSubscriptions.reduce((acc, sub) => {
      const nextPaymentDate = new Date(sub.current_period_end || sub.created_at);
      const key = nextPaymentDate.toDateString();

      if (!acc[key]) {
        acc[key] = {
          quantity: 0,
          monthly_cost: 0,
          next_payment_date: nextPaymentDate,
          status: sub.status,
          is_ending: false
        };
      }

      acc[key].quantity += sub.quantity;
      acc[key].monthly_cost += sub.quantity * 40;

      return acc;
    }, {} as Record<string, any>);

    // Add ending subscriptions (don't group these, show individually for clarity)
    const endingItems = endingSubscriptions.map(sub => ({
      quantity: sub.quantity,
      monthly_cost: sub.quantity * 40,
      next_payment_date: new Date(sub.current_period_end || sub.created_at),
      status: sub.status,
      is_ending: true,
      end_date: sub.license_end_date || sub.current_period_end
    }));

    // Combine and sort by date
    const allItems = [...Object.values(groupedActiveByDate), ...endingItems];
    return allItems.sort((a: any, b: any) =>
      a.next_payment_date.getTime() - b.next_payment_date.getTime()
    );
  })();

  // A cabinet is "new" if it's in trial and hasn't completed onboarding
  // This represents the initial setup phase
  const isNewCabinet = subscription?.status === 'trialing' && !subscription?.has_completed_onboarding;
  const isTrialing = subscriptions.some(sub => sub.status === 'trialing');
  const isActive = subscriptions.some(sub => sub.status === 'active');

  // For subscription validity:
  // - Active subscriptions are always valid
  // - Trialing subscriptions are only valid if user has already visited workspace (not first time)
  const hasValidSubscription = isActive || (isTrialing && !(memberMe?.workspace_first_time ?? false));

  // New calculated state variables for nuanced cancellation states (exclude canceled)
  const activeContinuingSubscriptions = subscriptions.filter(sub => !sub.cancel_at_period_end && sub.status === 'active');
  // Exclude trialing subscriptions from cancellation logic - they should not show "abonnement annulé"
  const nonTrialingSubscriptions = subscriptions.filter(sub => sub.status !== 'trialing');
  const areAllSubscriptionsEnding = nonTrialingSubscriptions.length > 0 && activeContinuingSubscriptions.length === 0;
  const totalMonthlyCostContinuing = activeContinuingSubscriptions.reduce((sum, sub) => sum + (sub.quantity * 40), 0);

  // Refine the billing breakdown to only include continuing subscriptions (exclude canceled)
  const billingBreakdownContinuing = (() => {
    if (!activeContinuingSubscriptions.length) return [];

    const groupedByDate = activeContinuingSubscriptions.reduce((acc, sub) => {
      const nextPaymentDate = new Date(sub.current_period_end || sub.created_at);
      const key = nextPaymentDate.toDateString();

      if (!acc[key]) {
        acc[key] = {
          quantity: 0,
          monthly_cost: 0,
          next_payment_date: nextPaymentDate,
          is_ending: false
        };
      }

      acc[key].quantity += sub.quantity;
      acc[key].monthly_cost += sub.quantity * 40;

      return acc;
    }, {} as Record<string, any>);

    return Object.values(groupedByDate).sort((a: any, b: any) =>
      a.next_payment_date.getTime() - b.next_payment_date.getTime()
    );
  })();

  const now = new Date().getTime();
  const trialEnd = subscription?.trial_end ? new Date(subscription.trial_end).getTime() : 0;
  const twentyFourHoursInMillis = 24 * 60 * 60 * 1000;

  const isTrialEndingSoon =
    subscription?.status === 'trialing' &&
    trialEnd > now &&
    (trialEnd - now) < twentyFourHoursInMillis;

  // Check if cabinet just upgraded from trial to active (first payment success)
  // This should trigger when status becomes 'active' but cabinet onboarding isn't marked complete yet
  // Only show to admin who made the payment
  const hasJustUpgraded =
    subscription?.status === 'active' &&
    !subscription?.has_completed_onboarding &&
    isAdmin; // Only show to admin who made the payment

  // Check if this is a first-time cabinet workspace visit for this user
  // We track this per-user, per-cabinet using the database field workspace_first_time
  const isFirstTimeInCabinet = (() => {
    if (!user || !cabinetId || !subscription) return false;

    // Check the database field workspace_first_time from cabinet member data
    // Only show if subscription is active/trialing and user hasn't seen welcome yet
    return (memberMe?.workspace_first_time ?? false) &&
           (subscription.status === 'trialing' || subscription.status === 'active');
  })();

  // Mark cabinet as visited by this user
  const markCabinetAsVisited = async () => {
    if (!user || !cabinetId) return;

    try {
      await apiClient.patch('/cabinet-members/mark-visited', {});

      // Invalidate the query to refresh the data
      queryClient.invalidateQueries({ queryKey: ["cabinet-member-data", cabinetId, user.id] });
      queryClient.invalidateQueries({ queryKey: [CABINET_MEMBER_ME_KEY] });
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'mark_cabinet_visited' }, silent: true });
    }
  };

  /**
   * No-op kept for API compatibility. `cabinet_subscriptions` is a Stripe
   * webhook-fed cache: onboarding/checkout flags are written by the backend
   * webhook handlers, never by the frontend (they used to be direct Supabase
   * writes — removed in P1b/P4, 2026-08-12).
   */
  const markOnboardingComplete = async () => {};

  /** No-op — same webhook-ownership rationale as {@link markOnboardingComplete}. */
  const markFirstCheckoutComplete = async () => {};

  const isTrialExpired =
    subscription?.status === 'trialing' &&
    trialEnd > 0 &&
    trialEnd < now;

  /**
   * No-op kept for API compatibility. The Stripe Customer is created lazily by
   * the backend on the first checkout (POST /api/stripe/create-checkout-session)
   * — there is no standalone "create customer" call anymore (the Supabase Edge
   * Function create-cabinet-customer is retired, P1b/P4 2026-08-12).
   */
  const createCabinetCustomer = () => {};

  /**
   * Redirect to the Stripe-hosted Checkout for `quantity` monthly seats.
   * Price ids and success/cancel URLs are resolved server-side — the frontend
   * only states the intent `{quantity, plan}` and navigates to the returned
   * hosted url.
   */
  const redirectToCabinetCheckout = async (quantity: number) => {
    if (!isAdmin) {
      throw new Error("Seuls les administrateurs du cabinet peuvent gérer la facturation (checkout Stripe refusé côté client).");
    }

    setLoading(true);
    try {
      const { url } = await apiClient.post<CheckoutSessionResponse>('/stripe/create-checkout-session', {
        quantity,
        plan: 'monthly',
      });
      window.location.assign(url);
    } finally {
      setLoading(false);
    }
  };

  // Manage licenses (add only)
  const manageLicensesMutation = useMutation({
    mutationFn: async ({ count }: { action: 'add'; count: number }) => {
      if (!isAdmin) {
        throw new Error("Seuls les administrateurs du cabinet peuvent gérer les licences (ajout refusé côté client).");
      }

      // Use checkout for license additions to create separate billing cycles
      await redirectToCabinetCheckout(count);
      return { success: true, message: 'Redirecting to checkout...' };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cabinet-subscriptions", cabinetId] });
      queryClient.invalidateQueries({ queryKey: ["license-usage", cabinetId] });
    },
  });

  // Open customer portal (Stripe-hosted; backend builds the session + return_url)
  const openCustomerPortal = async () => {
    if (!isAdmin) {
      throw new Error("Seuls les administrateurs du cabinet peuvent accéder au portail de facturation.");
    }

    try {
      const { url } = await apiClient.post<PortalSessionResponse>('/stripe/create-portal-session', {});
      window.location.assign(url);
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'open_customer_portal' }, silent: true });
      throw error;
    }
  };

  /**
   * No-op returning `true` for API compatibility — the onboarding flag lives in
   * `cabinet_subscriptions`, owned by the Stripe webhooks (see
   * {@link markOnboardingComplete}).
   */
  const completeCabinetOnboarding = async () => true;

  // Handle special cases after all hooks have been called

  // If no cabinet ID yet, return loading state (polling is happening in useCurrentCabinetId)
  if (!cabinetId && !isOnProfileCompletionPage) {
    // CAST: type-widen empty literals for return type inference
    return {
      subscription: null as CabinetSubscription | null,
      licenseUsage: null as LicenseUsage | null,
      isAdmin: false,
      isNewCabinet: false,
      isTrialing: false,
      isActive: false,
      hasValidSubscription: false,
      isTrialEndingSoon: false,
      isTrialExpired: false,
      isLoading: true, // Show as loading while cabinet ID is being fetched
      checkoutLoading: false,
      createCabinetCustomer: () => {},
      redirectToCabinetCheckout: (_quantity: number) => Promise.resolve(),
      manageLicenses: () => {},
      openCustomerPortal: () => {},
      completeCabinetOnboarding: async () => true,
      isCreatingCustomer: false,
      isManagingLicenses: false,
      refetch: () => {},
      // Additional polling state info
      subscriptionQuery: { isFetching: false, isRefetching: false, isFetched: false },
      subscriptionsQuery: { isFetching: false, isRefetching: false, isFetched: false },
      licenseUsageQuery: { isFetching: false, isRefetching: false, isFetched: false },
      cabinetMemberQuery: { isFetching: false, isRefetching: false, isFetched: true },
      // New data placeholders
      subscriptions: [] as CabinetSubscription[],
      totalLicenses: 0,
      billingBreakdown: [] as Array<{ quantity: number; monthly_cost: number; next_payment_date: Date; status: string; is_ending: boolean; end_date?: string | null }>,
      billingBreakdownContinuing: [] as Array<{ quantity: number; monthly_cost: number; next_payment_date: Date; status: string; is_ending: boolean }>,
      activeContinuingSubscriptions: [] as CabinetSubscription[],
      areAllSubscriptionsEnding: false,
      totalMonthlyCostContinuing: 0,
      // Placeholder states
      wasInvited: false,
      hasCompletedOnboarding: false,
      hasJustUpgraded: false,
      isFirstTimeInCabinet: false,
      markOnboardingComplete: async () => {},
      markFirstCheckoutComplete: async () => {},
      markCabinetAsVisited: async () => {}
    };
  }

  // During profile completion, return safe defaults
  if (isOnProfileCompletionPage) {
    return {
      subscription: null,
      licenseUsage: null,
      isAdmin: false,
      isNewCabinet: false,
      isTrialing: false,
      isActive: false,
      hasValidSubscription: true, // Assume valid during profile completion
      isTrialEndingSoon: false,
      isTrialExpired: false,
      isLoading: false,
      checkoutLoading: false,
      createCabinetCustomer: () => {},
      redirectToCabinetCheckout: (_quantity: number) => Promise.resolve(),
      manageLicenses: () => {},
      openCustomerPortal: () => {},
      completeCabinetOnboarding: async () => true,
      isCreatingCustomer: false,
      isManagingLicenses: false,
      refetch: () => {},
      subscriptionQuery: { isFetching: false, isRefetching: false, isFetched: true },
      subscriptionsQuery: { isFetching: false, isRefetching: false, isFetched: true },
      licenseUsageQuery: { isFetching: false, isRefetching: false, isFetched: true },
      cabinetMemberQuery: { isFetching: false, isRefetching: false, isFetched: true },
      // Safe defaults
      subscriptions: [],
      totalLicenses: 0,
      billingBreakdown: [],
      billingBreakdownContinuing: [],
      activeContinuingSubscriptions: [],
      areAllSubscriptionsEnding: false,
      totalMonthlyCostContinuing: 0,
      wasInvited: false,
      hasCompletedOnboarding: true,
      hasJustUpgraded: false,
      isFirstTimeInCabinet: false,
      markOnboardingComplete: async () => {},
      markFirstCheckoutComplete: async () => {},
      markCabinetAsVisited: async () => {}
    };
  }

  return {
    // Data
    subscription,
    licenseUsage,
    isAdmin,
    wasInvited,
    hasCompletedOnboarding,

    // Computed states
    isNewCabinet,
    isTrialing,
    isActive,
    hasValidSubscription,
    isTrialEndingSoon,
    isTrialExpired,

    // Cabinet-specific dialog states
    hasJustUpgraded,
    isFirstTimeInCabinet,

    // Loading states
    isLoading: subscriptionsQuery.isLoading || licenseUsageQuery.isLoading,
    checkoutLoading: loading,

    // Query states for more granular loading control
    subscriptionsQuery,
    subscriptionQuery: subscriptionsQuery, // For backward compatibility
    licenseUsageQuery,
    cabinetMemberQuery: { isFetching: false, isRefetching: false, isFetched: true },

    // New data for multiple subscriptions
    subscriptions,
    totalLicenses,
    billingBreakdown,
    billingBreakdownContinuing,
    activeContinuingSubscriptions,
    areAllSubscriptionsEnding,
    totalMonthlyCostContinuing,

    // Actions
    createCabinetCustomer,
    redirectToCabinetCheckout,
    manageLicenses: manageLicensesMutation.mutate,
    openCustomerPortal,
    completeCabinetOnboarding,
    markOnboardingComplete,
    markFirstCheckoutComplete,
    markCabinetAsVisited,

    // Mutation states
    isCreatingCustomer: false, // customer creation is server-side, inside checkout
    isManagingLicenses: manageLicensesMutation.isPending,

    // Refetch functions
    refetch: () => {
      subscriptionsQuery.refetch();
      licenseUsageQuery.refetch();
    }
  };
};

/** Helper hook to get current cabinet ID — now delegates to centralized hook */
export const useCurrentCabinetId = () => {
  return useCabinetId();
};
